/**
 * WatchlistPlayerCard container — binds one pool player to the compact card.
 *
 * ## Why the drag shell is still `SidebarPlayerCard`
 *
 * Only the LOOK changed in SE-6554, not the interaction. Dragging a card from
 * this list onto the board is matched by the board's drop handlers on the
 * dnd-kit draggable id (`sidebar-<playerId>`) and the `source: "sidebar"`
 * payload, and it is disabled once the player is already placed. Re-declaring
 * that draggable here would be a second copy of a contract whose two halves
 * live in different files — so this renders `SidebarPlayerCard` and passes the
 * compact body into it instead.
 *
 * ## Why this is a container and not a closure in the list
 *
 * The list renders these in a `map`, and `react-perf` bans building the
 * per-item closures (`renderBody`, the press handler) there. Binding them here
 * gives every card stable handler identities — the same reason
 * `AddPlayersRow` has a container.
 * @module features/player-kanban/components/molecules/AddPlayersRow
 */

import React, { useCallback } from "react";

import SidebarPlayerCard from "@/components/organisms/custom/ui/SidebarPlayerCard";
import type { PlayerKanbanPlayerFragmentFragment } from "@/generated/graphql";
import { formatGeminiValuationRange } from "@/utils/formatCurrencyShort";

import {
  formatCandidateGpr,
  formatCandidateName,
  formatRosterMeta,
  formatWatchlistMeta,
} from "../../../../utils/addPlayersRowMeta";
import {
  getPlayerAge,
  getPlayerArchetype,
  getPlayerContractMonths,
  getPlayerTeamName,
} from "../../../../utils/manualPlayerUtils";

import WatchlistPlayerCardView from "./WatchlistPlayerCardView";

/** Props for the WatchlistPlayerCard container. */
export interface WatchlistPlayerCardContainerProps {
  /** The pool player to render. */
  readonly player: PlayerKanbanPlayerFragmentFragment;
  /** Whether this player already sits on the board (disables the drag). */
  readonly isOnKanbanBoard: boolean;
  /** The stage the player is in, when placed. */
  readonly kanbanPhaseName?: string;
  /**
   * Which pool surfaced this card (SE-6952). Drives the meta line: the
   * watchlist variant reads club · age · contract, the roster variant reads
   * age · squad status (Academy / First Team).
   */
  readonly metaVariant?: "watchlist" | "roster";
  /** Whether an add for this player is in flight (SE-6951). */
  readonly isAdding?: boolean;
  /** Add this player to the pipeline (SE-6951). Absent hides the action. */
  readonly onAdd?: (playerId: string) => void;
  /** Open the player. */
  readonly onPress: () => void;
  /** Drag start callback. */
  readonly onDragStart?: (dragData?: unknown, event?: unknown) => void;
  /** Drag end callback. */
  readonly onDragEnd?: () => void;
  /** Auto-scroll callback during drag. */
  readonly onAutoScroll?: (dragX: number) => void;
}

/**
 * Render one draggable compact watchlist/roster card.
 * @param root0 - Component props.
 * @param root0.player - The pool player.
 * @param root0.isOnKanbanBoard - Whether the player is already placed.
 * @param root0.kanbanPhaseName - The stage the player is in, when placed.
 * @param root0.metaVariant - Which pool surfaced the card (SE-6952).
 * @param root0.isAdding - Whether an add is in flight (SE-6951).
 * @param root0.onAdd - Add callback (SE-6951).
 * @param root0.onPress - Open-player callback.
 * @param root0.onDragStart - Drag start callback.
 * @param root0.onDragEnd - Drag end callback.
 * @param root0.onAutoScroll - Auto-scroll callback.
 */
const WatchlistPlayerCardContainer = ({
  player,
  isOnKanbanBoard,
  kanbanPhaseName,
  metaVariant = "watchlist",
  isAdding = false,
  onAdd,
  onPress,
  onDragStart,
  onDragEnd,
  onAutoScroll,
}: WatchlistPlayerCardContainerProps) => {
  const contractMonths = getPlayerContractMonths(player);

  const name = formatCandidateName(player);
  const meta =
    metaVariant === "roster"
      ? formatRosterMeta(player, player.isAcademy)
      : formatWatchlistMeta(player);
  const gpr = formatCandidateGpr(player);

  const handleAdd = useCallback(
    () => (onAdd === undefined ? undefined : onAdd(player.id)),
    [onAdd, player.id]
  );

  const renderBody = useCallback(
    () => (
      <WatchlistPlayerCardView
        playerId={player.id}
        name={name}
        meta={meta}
        gpr={gpr}
        isInList={isOnKanbanBoard}
        isAdding={isAdding}
        onAdd={onAdd === undefined ? undefined : handleAdd}
      />
    ),
    [gpr, handleAdd, isAdding, isOnKanbanBoard, meta, name, onAdd, player.id]
  );

  /*
   * The drag payload stays the LEGACY shape, in full.
   *
   * The compact card shows fewer fields than the old row did, but the board's
   * drag overlay and its drop handler read this payload — not the card — so
   * trimming it to what the card displays would empty the overlay mid-drag.
   * The valuation range in particular is dropped from the CARD by the design
   * and kept HERE for exactly that reason.
   */
  return (
    <SidebarPlayerCard
      playerId={player.id}
      playerName={player.name ?? ""}
      contractValue={
        formatGeminiValuationRange(
          player.bioData?.minGeminiPlayerValuation,
          player.bioData?.maxGeminiPlayerValuation
        ) ?? "N/A"
      }
      contractMonths={contractMonths}
      teamName={getPlayerTeamName(player)}
      age={getPlayerAge(player)}
      archetype={getPlayerArchetype(player)}
      gpr={Math.round(player.bioData?.gpr || 0)}
      isOnKanbanBoard={isOnKanbanBoard}
      kanbanPhaseName={kanbanPhaseName}
      onPress={onPress}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onAutoScroll={onAutoScroll}
      renderBody={renderBody}
    />
  );
};

WatchlistPlayerCardContainer.displayName = "WatchlistPlayerCardContainer";

export default WatchlistPlayerCardContainer;
